/**
 * Lenguaje humano para fechas y dinero.
 *
 * La app decía "vence el 15/08/2026" y esperaba que el usuario hiciera la resta
 * mentalmente. Nadie piensa así: piensa "mañana", "el viernes", "en dos semanas".
 */

const DIAS = ["domingo", "lunes", "martes", "miércoles", "jueves", "viernes", "sábado"];
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function atMidnight(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function parseDay(iso) {
  if (!iso) return null;
  // "2026-08-15" y "2026-08-15T..." se leen como día local, no UTC: si no, un
  // vencimiento de hoy se pinta como "ayer" al oeste de Greenwich.
  const [y, m, d] = String(iso).slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function daysUntil(iso) {
  const target = parseDay(iso);
  if (!target) return null;
  return Math.round((atMidnight(target) - atMidnight(new Date())) / 86400000);
}

/** "Hoy" · "Mañana" · "El viernes" · "15 de agosto" · "Hace 3 días" */
export function whenLabel(iso) {
  const n = daysUntil(iso);
  if (n === null) return "";
  if (n === 0) return "Hoy";
  if (n === 1) return "Mañana";
  if (n === -1) return "Ayer";
  if (n < -1) return `Hace ${Math.abs(n)} días`;
  if (n < 7) return `El ${DIAS[parseDay(iso).getDay()]}`;
  const d = parseDay(iso);
  return `${d.getDate()} de ${MESES[d.getMonth()]}`;
}

/** Versión corta para listas densas: "hoy", "mañana", "en 5 días", "vencido". */
export function whenShort(iso) {
  const n = daysUntil(iso);
  if (n === null) return "";
  if (n < 0) return `vencido hace ${Math.abs(n)} ${Math.abs(n) === 1 ? "día" : "días"}`;
  if (n === 0) return "vence hoy";
  if (n === 1) return "vence mañana";
  if (n < 7) return `vence el ${DIAS[parseDay(iso).getDay()]}`;
  return `vence en ${n} días`;
}

/** Encabezado de grupo en la lista de movimientos. */
export function dayHeading(iso) {
  const n = daysUntil(iso);
  if (n === 0) return "Hoy";
  if (n === -1) return "Ayer";
  const d = parseDay(iso);
  if (!d) return "";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `${d.getDate()} de ${MESES[d.getMonth()]}${sameYear ? "" : ` de ${d.getFullYear()}`}`;
}

/**
 * Dinero sin decimales cuando son ",00".
 * Las cifras grandes se leen mejor sin ruido: RD$120,400 en vez de RD$120,400.00
 */
export function money(n, currency = "DOP", { decimals = "auto" } = {}) {
  const v = Number(n || 0);
  const s = ({
    DOP: "RD$", USD: "US$", EUR: "€", USDT: "USDT ",
    MXN: "MX$", COP: "CO$", ARS: "AR$", CLP: "CL$", PEN: "S/", BRL: "R$",
  })[String(currency || "DOP").toUpperCase()] || `${currency} `;
  const showDecimals = decimals === "always" || (decimals === "auto" && v % 1 !== 0);
  const abs = Math.abs(v).toLocaleString("es-DO", {
    minimumFractionDigits: showDecimals ? 2 : 0,
    maximumFractionDigits: showDecimals ? 2 : 0,
  });
  return `${v < 0 ? "−" : ""}${s}${abs}`;
}

/**
 * Traduce un error del backend a algo que una persona entienda.
 *
 * El backend ya devuelve mensajes en español, pero algunos siguen siendo de
 * ingeniería (422 de Pydantic, "subscription_required"). Esto los ataja y les
 * pone además una salida: qué puede hacer el usuario ahora.
 */
export function humanError(e) {
  const status = e?.response?.status;
  const d = e?.response?.data?.detail;
  const raw = typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "") : "";

  if (status === 409) {
    return {
      title: "Esto es parte de un movimiento más grande",
      body: raw || "Para corregirlo, deshaz la operación completa desde su propia sección.",
      action: null,
    };
  }
  if (status === 422) {
    return {
      title: "Faltan datos",
      body: "Revisa que el monto y los campos obligatorios estén completos.",
      action: null,
    };
  }
  if (/límite/i.test(raw)) {
    return {
      title: "Esa tarjeta ya llegó a su límite",
      body: raw,
      action: "Usa otra tarjeta o paga desde una cuenta.",
    };
  }
  if (/[Ss]aldo insuficiente/.test(raw)) {
    return {
      title: "No hay suficiente en esa cuenta",
      body: raw,
      action: "Elige otra cuenta o baja el monto.",
    };
  }
  if (!e?.response) {
    return {
      title: "Sin conexión",
      body: "No pude comunicarme con el servidor.",
      action: "Revisa tu internet y vuelve a intentar.",
    };
  }
  return { title: raw || "No pude guardarlo", body: "", action: null };
}

/** Una línea, para toasts. */
export function humanErrorLine(e) {
  const h = humanError(e);
  return [h.title, h.body, h.action].filter(Boolean).join(" ");
}
